// CreateLicenseForm.js
import React, { useState } from 'react';
import { useAuth } from "react-oidc-context";
import { licenseService } from './apiService';
import { COUNTRY_CODES, isValidCountryCode } from './qrCodeUtils';
import { Button } from './components/Button';
import './forms.css';

function CreateLicenseForm({ onLicenseCreated }) {
  const [serialNumber, setSerialNumber] = useState('');
  const [licenseTier, setLicenseTier] = useState('BASIC'); // Default tier
  const [customerName, setCustomerName] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [country, setCountry] = useState('');
  const [durationMonths, setDurationMonths] = useState(12); // Default 12 months
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Grab the auth context to get the access token
  const auth = useAuth();

  const getExpiryPreview = () => {
    const now = new Date();
    const preview = new Date(now);
    preview.setMonth(preview.getMonth() + parseInt(durationMonths));
    // Set to end of day
    preview.setHours(23, 59, 59, 999);
    return preview.toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!serialNumber) {
       setMessage("Serial number cannot be empty.");
       return;
    }
    
    // Validate country code if provided
    if (country && !isValidCountryCode(country)) {
       setMessage("Invalid country code. Please use 2-letter ISO code (e.g., US, GB).");
       return;
    }
    
    setMessage(''); // Clear previous messages
    setLoading(true);

    try {
      const token = auth.user?.access_token;
      if (!token) {
        throw new Error("No access token found. Please log in.");
      }

      const licenseData = { 
        serialNumber, 
        licenseTier,
        customerName: customerName.trim() || undefined,
        customerNumber: customerNumber.trim() || undefined,
        durationMonths: parseInt(durationMonths) // Include duration in API call
      };
      
      // Only include optional fields if they have values
      if (country) licenseData.country = country.toUpperCase();
      
      const result = await licenseService.createLicense(licenseData, token);

      setMessage('License created successfully!');
      if (result.license && onLicenseCreated) {
        onLicenseCreated(result.license); // Notify parent
      }
      // Reset form
      setSerialNumber('');
      setLicenseTier('BASIC');
      setCustomerName('');
      setCustomerNumber('');
      setCountry('');
      setDurationMonths(12); // Reset to default
    } catch (err) {
       // Use error message from apiService
      setMessage(`Error: ${err.message}`);
      // Log the full error for debugging if needed
      console.error("Create license failed:", err);
    } finally {
       setLoading(false);
    }
  };
  return (
    <section className="form-card">
      <h2 className="form-card__title">Create New License</h2>
      <form onSubmit={handleCreate} className="form-grid">
        <div className="form-field">
          <label htmlFor="serialNumber" className="form-label">Serial Number</label>
          <input
            id="serialNumber"
            type="text"
            className="form-input"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            required
            aria-required="true"
            disabled={loading}
          />
        </div>
        <div className="form-field">
          <label htmlFor="licenseTier" className="form-label">License Tier</label>
          <select
            id="licenseTier"
            className="form-select"
            value={licenseTier}
            onChange={(e) => setLicenseTier(e.target.value)}
            disabled={loading}
          >
            <option value="BASIC">BASIC</option>
            <option value="PREMIUM">PREMIUM</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="durationMonths" className="form-label">License Duration</label>
          <select
            id="durationMonths"
            className="form-select"
            value={durationMonths}
            onChange={(e) => setDurationMonths(e.target.value)}
            disabled={loading}
          >
            <option value="1">1 month</option>
            <option value="3">3 months</option>
            <option value="6">6 months</option>
            <option value="12">12 months</option>
            <option value="24">24 months</option>
            <option value="36">36 months</option>
            <option value="60">60 months</option>
          </select>
          <span className="form-hint">
            Expires: {getExpiryPreview()}
          </span>
        </div>
        <div className="form-field">
          <label htmlFor="customerName" className="form-label">Customer Name</label>
          <input
            id="customerName"
            type="text"
            className="form-input"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            disabled={loading}
          />
        </div>
        <div className="form-field">
          <label htmlFor="customerNumber" className="form-label">Customer Number</label>
          <input
            id="customerNumber"
            type="text"
            className="form-input"
            value={customerNumber}
            aria-describedby="customerNumberHelp"
            maxLength={50}
            pattern="[A-Za-z0-9-]{1,50}"
            onChange={(e) => setCustomerNumber(e.target.value)}
            disabled={loading}
          />
          <p id="customerNumberHelp">Institution identifier. Once assigned, it cannot change. Unassigned licences need verified commissioning before archive access.</p>
        </div>
        <div className="form-field">
          <label htmlFor="country" className="form-label">Country</label>
          <select
            id="country"
            className="form-select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={loading}
          >
            <option value="">Select Country (Optional)</option>
            {COUNTRY_CODES.map(({ code, name }) => (
              <option key={code} value={code}>{name} ({code})</option>
            ))}
          </select>
        </div>
        <div className="form-actions">
          <Button type="submit" variant="primary" disabled={loading} loading={loading}>
            Create License
          </Button>
        </div>
      </form>
      {message && (
        <p
          role="alert"
          className={`form-message ${message.startsWith('Error:') ? 'form-message--error' : 'form-message--success'}`}
        >
          {message}
        </p>
      )}
    </section>
  );
}

export default CreateLicenseForm;
